import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../../api/client';
import { date, fine, money } from '../../utils/format';
import { Chip, Empty, ErrorNotice, Loading, PageHeader, Table } from '../../components/ui';
import { DateInput } from '../../components/ui/Form';

interface Melter {
  melter_key: string;
  melter: string;
  refiner_id: string | null;
  melter_type: string;
  metal_id: string;
  metal_name: string;
  melts: number;
  completed: number;
  fine_sent: string;
  fine_in_bars: string;
  fine_sample_dust: string;
  fee_fine: string;
  loss_fine: string;
  loss_pct: string | null;
  allowed_pct: string | null;
  worst_loss_pct: string | null;
  over_allowance: number;
  excess_fine: string;
  excess_value: string;
  recovered_amount: string;
  charges: string;
  charges_tax: string;
  out_count: number;
  out_fine: string;
  out_cost: string;
  overdue: number;
  most_overdue_days: number | null;
  avg_days: string | null;
  last_melt: string | null;
  account_balance: string | null;
}

interface MeltRow {
  id: string;
  doc_no: string;
  melt_date: string;
  received_date: string | null;
  status: string;
  metal_name: string;
  melter: string;
  refiner_id: string | null;
  input_fine_wt: string;
  output_fine_wt: string | null;
  charges_fine_wt: string;
  loss_fine_wt: string | null;
  loss_pct: string | null;
  allowed_loss_pct: string;
  excess_loss_value: string;
  recovered_amount: string;
  charges: string;
  bar_nos: string | null;
  expected_return_date: string | null;
  days_overdue: number;
}

const n = (v: string | number | null | undefined): number => Number(v || 0);
const R = (label: string) => ({ label, align: 'right' as const });

/** Loss beside its allowance; a third place when the two round alike but the loss is over. */
const pct = (value: number, allowed: number): string =>
  value > allowed && value.toFixed(2) === allowed.toFixed(2) ? `${value.toFixed(3)}%` : `${value.toFixed(2)}%`;

const STATUS: Record<string, { label: string; tone: 'good' | 'warn' | 'bad' | 'mute' }> = {
  IN_PROCESS: { label: 'Out', tone: 'warn' },
  PENDING_APPROVAL: { label: 'Awaiting approval', tone: 'bad' },
  POSTED: { label: 'Completed', tone: 'good' },
  CANCELLED: { label: 'Cancelled', tone: 'mute' },
};

/**
 * Every karigar and refiner the shop melts with, and how each one does.
 *
 * A shop sends metal to the same few people for years; this is where it sees,
 * melter by melter and metal by metal, how much went, how much came back, how
 * the loss compares with what was agreed, what is still with them and for how
 * long, what they were paid — in money and in metal — and what was charged
 * back to them for loss above the allowance. A refiner on the books also shows
 * what the shop owes them right now.
 */
export function MeltersPage() {
  const navigate = useNavigate();
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [open, setOpen] = useState<string | null>(null);

  const qs = new URLSearchParams();
  if (from) qs.set('from', from);
  if (to) qs.set('to', to);
  const melters = useQuery({
    queryKey: ['melters', from, to],
    queryFn: () => api.get<{ data: Melter[] }>(`/meltings/melters${qs.toString() ? `?${qs}` : ''}`),
  });
  const melts = useQuery({
    queryKey: ['meltings'],
    queryFn: () => api.get<{ data: MeltRow[] }>('/meltings?limit=500'),
  });

  const rows = melters.data?.data ?? [];
  const selected = rows.find((r) => `${r.melter_key}|${r.metal_id}` === open) ?? null;

  // A melter's own melts, for the statement under their row.
  const theirMelts = useMemo(() => {
    if (!selected) return [];
    return (melts.data?.data ?? []).filter(
      (m) =>
        m.metal_name === selected.metal_name &&
        m.status !== 'CANCELLED' &&
        (selected.refiner_id ? m.refiner_id === selected.refiner_id : !m.refiner_id && m.melter === selected.melter) &&
        (!from || m.melt_date.slice(0, 10) >= from) &&
        (!to || m.melt_date.slice(0, 10) <= to),
    );
  }, [selected, melts.data, from, to]);

  return (
    <>
      <PageHeader
        title="Melters and refiners"
        subtitle="Each karigar and refiner, metal by metal: what went, what came back, the loss against the allowance, and what is still with them."
        actions={
          <div className="flex gap-2">
            <Link className="btn-ghost" to="/melting">
              Melting
            </Link>
            <Link className="btn-ghost" to="/reports?group=Melting">
              Reports
            </Link>
          </div>
        }
      />
      <ErrorNotice error={melters.error} />

      <div className="mb-4 flex flex-wrap items-end gap-3">
        <label className="text-2xs text-ink-soft">
          Melts sent from
          <DateInput value={from} onChange={setFrom} />
        </label>
        <label className="text-2xs text-ink-soft">
          To
          <DateInput value={to} onChange={setTo} />
        </label>
        {from || to ? (
          <button
            className="btn-quiet text-xs"
            onClick={() => {
              setFrom('');
              setTo('');
            }}
          >
            All time
          </button>
        ) : null}
        <p className="ml-auto max-w-md text-right text-2xs text-ink-soft">
          Loss is what did not come back as bars, sample or dust, after any fee kept in metal. Loss % is weighed by the
          metal sent, so one big melt counts for more than a small one.
        </p>
      </div>

      {melters.isLoading ? <Loading /> : null}
      {!melters.isLoading && rows.length === 0 ? (
        <Empty message="No melts yet." hint="Once metal is sent for melting, each melter's record appears here." />
      ) : null}

      {rows.length > 0 ? (
        <Table
          head={[
            'Melter / refiner',
            'Metal',
            R('Melts'),
            R('Fine sent'),
            R('Came back'),
            R('Loss'),
            R('Loss % / allowed'),
            R('Over'),
            R('Charged back'),
            R('Charges'),
            R('With them now'),
            R('Owed to them'),
          ]}
        >
          {rows.map((r) => {
            const key = `${r.melter_key}|${r.metal_id}`;
            const back = n(r.fine_in_bars) + n(r.fine_sample_dust);
            const over = n(r.loss_pct) > n(r.allowed_pct) + 1e-9;
            return (
              <tr
                key={key}
                className={`cursor-pointer hover:bg-gold-50 ${open === key ? 'bg-gold-50' : ''}`}
                onClick={() => setOpen(open === key ? null : key)}
              >
                <td className="td">
                  <span className="font-medium">{r.melter}</span>
                  <span className="block text-2xs text-ink-soft">
                    {r.refiner_id ? 'Refiner on the books' : 'In-house / karigar'}
                    {r.last_melt ? ` · last ${date(r.last_melt)}` : ''}
                  </span>
                </td>
                <td className="td">{r.metal_name}</td>
                <td className="td num text-right">
                  {r.melts}
                  {r.completed !== r.melts ? (
                    <span className="block text-2xs text-ink-soft">{r.completed} done</span>
                  ) : null}
                </td>
                <td className="td num whitespace-nowrap text-right">{r.completed > 0 ? fine(r.fine_sent) : '—'}</td>
                <td className="td num whitespace-nowrap text-right">
                  {r.completed > 0 ? fine(back.toFixed(4)) : '—'}
                  {n(r.fee_fine) > 0 ? (
                    <span className="block text-2xs text-ink-soft">+ {fine(r.fee_fine)} fee</span>
                  ) : null}
                </td>
                <td className="td num whitespace-nowrap text-right">{r.completed > 0 ? fine(r.loss_fine) : '—'}</td>
                <td className={`td num whitespace-nowrap text-right ${over ? 'font-semibold text-danger' : ''}`}>
                  {r.loss_pct !== null ? (
                    <>
                      {pct(n(r.loss_pct), n(r.allowed_pct))}
                      <span className="font-normal text-ink-faint"> / {n(r.allowed_pct).toFixed(2)}%</span>
                      {r.worst_loss_pct !== null && r.completed > 1 ? (
                        <span className="block text-2xs font-normal text-ink-soft">
                          worst {n(r.worst_loss_pct).toFixed(2)}%
                        </span>
                      ) : null}
                    </>
                  ) : (
                    '—'
                  )}
                </td>
                <td className="td num text-right">
                  {r.over_allowance > 0 ? (
                    <Chip tone="bad">{r.over_allowance}</Chip>
                  ) : (
                    <span className="text-ink-faint">0</span>
                  )}
                </td>
                <td className="td num whitespace-nowrap text-right">
                  {n(r.recovered_amount) > 0 ? <span className="text-success">{money(r.recovered_amount)}</span> : '—'}
                  {n(r.excess_value) > 0 ? (
                    <span className="block text-2xs text-ink-soft">excess {money(r.excess_value)}</span>
                  ) : null}
                </td>
                <td className="td num whitespace-nowrap text-right">{n(r.charges) > 0 ? money(r.charges) : '—'}</td>
                <td className="td num whitespace-nowrap text-right">
                  {r.out_count > 0 ? (
                    <>
                      {fine(r.out_fine)}
                      <span className="block text-2xs text-ink-soft">
                        {r.out_count} melt{r.out_count === 1 ? '' : 's'}
                        {r.overdue > 0 ? <span className="text-danger"> · {r.overdue} late</span> : null}
                      </span>
                    </>
                  ) : (
                    <span className="text-ink-faint">nothing</span>
                  )}
                  {r.avg_days !== null ? (
                    <span className="block text-2xs text-ink-soft">
                      {n(r.avg_days) < 1
                        ? 'usually back the same day'
                        : `usually back in ${n(r.avg_days).toFixed(1)} days`}
                    </span>
                  ) : null}
                </td>
                <td className="td num whitespace-nowrap text-right">
                  {r.account_balance === null ? (
                    <span className="text-ink-faint">—</span>
                  ) : n(r.account_balance) < 0 ? (
                    <span className="text-success">they owe {money(Math.abs(n(r.account_balance)).toFixed(2))}</span>
                  ) : (
                    money(r.account_balance)
                  )}
                </td>
              </tr>
            );
          })}
        </Table>
      ) : null}

      {selected ? (
        <section className="mt-6">
          <div className="mb-2 flex flex-wrap items-end justify-between gap-2">
            <div>
              <h2 className="text-base font-semibold">
                {selected.melter} — {selected.metal_name}
              </h2>
              <p className="text-sm text-ink-soft">
                Every melt with them{from || to ? ' in these dates' : ''}. Open one to see its bars, assay and books.
              </p>
            </div>
            {selected.refiner_id ? (
              <Link className="btn-ghost text-sm" to="/reports?report=supplier-outstanding">
                Supplier balances
              </Link>
            ) : null}
          </div>
          {theirMelts.length === 0 ? (
            <Empty message="No melts in these dates." />
          ) : (
            <Table
              head={[
                'Sent',
                'Back',
                'Melt',
                R('Fine sent'),
                R('In bars'),
                R('Fee in metal'),
                R('Loss'),
                R('Loss %'),
                R('Charged back'),
                R('Charges'),
                { label: 'Bars', align: 'left' },
                'Status',
              ]}
            >
              {theirMelts.map((m) => {
                const over = n(m.loss_pct) > n(m.allowed_loss_pct);
                return (
                  <tr
                    key={m.id}
                    className="cursor-pointer hover:bg-gold-50"
                    onClick={() => navigate(`/melting?tab=${m.status === 'POSTED' ? 'POSTED' : m.status}&open=${m.id}`)}
                  >
                    <td className="td">{date(m.melt_date)}</td>
                    <td className="td">
                      {m.received_date ? (
                        date(m.received_date)
                      ) : m.days_overdue > 0 ? (
                        <span className="text-danger">{m.days_overdue}d late</span>
                      ) : m.expected_return_date ? (
                        <span className="text-ink-soft">due {date(m.expected_return_date)}</span>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td className="td num">{m.doc_no}</td>
                    <td className="td num text-right">{fine(m.input_fine_wt)}</td>
                    <td className="td num text-right">{m.output_fine_wt ? fine(m.output_fine_wt) : '—'}</td>
                    <td className="td num text-right">{n(m.charges_fine_wt) > 0 ? fine(m.charges_fine_wt) : '—'}</td>
                    <td className="td num text-right">{m.loss_fine_wt ? fine(m.loss_fine_wt) : '—'}</td>
                    <td className={`td num text-right ${over ? 'font-semibold text-danger' : ''}`}>
                      {m.loss_pct ? `${n(m.loss_pct).toFixed(2)}%` : '—'}
                    </td>
                    <td className="td num text-right">{n(m.recovered_amount) > 0 ? money(m.recovered_amount) : '—'}</td>
                    <td className="td num text-right">{n(m.charges) > 0 ? money(m.charges) : '—'}</td>
                    <td className="td num text-xs">{m.bar_nos ?? '—'}</td>
                    <td className="td">
                      <Chip tone={STATUS[m.status]?.tone ?? 'mute'}>{STATUS[m.status]?.label ?? m.status}</Chip>
                    </td>
                  </tr>
                );
              })}
            </Table>
          )}
        </section>
      ) : rows.length > 0 ? (
        <p className="mt-3 text-sm text-ink-soft">Click a melter to see every melt with them.</p>
      ) : null}
    </>
  );
}
